import Link from 'next/link';
import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Home2025Hero } from '../../types/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../responsive';
import { Button } from '../ui/button';
import { getAssetUrl } from '../../utils/contentstack';
import { HeroEyebrow, HeroHeadline, HeroSubheadline } from '../section';

export const HomeHero: FunctionComponent<Home2025Hero> = ({
  eyebrow,
  headline,
  subheadline,
  primary_button,
  secondary_button,
  background_videoConnection,
  background_video_posterConnection,
}) => {
  const posterUrl = getAssetUrl(background_video_posterConnection);
  const videoUrl = getAssetUrl(background_videoConnection);

  return (
    <Hero id="home-hero">
      {videoUrl && (
        <VideoWrapper aria-hidden="true">
          <video
            className="bg-video"
            autoPlay
            playsInline
            loop
            muted
            preload="auto"
            poster={posterUrl}
          >
            <source src={videoUrl} />
          </video>
        </VideoWrapper>
      )}

      <HeroContent>
        <ResponsiveContainer>
          <HeroEyebrow>{eyebrow}</HeroEyebrow>
          <HeroHeadline dangerouslySetInnerHTML={{ __html: headline || '' }} />
          <HeroSubheadline
            dangerouslySetInnerHTML={{ __html: subheadline || '' }}
          />

          <ButtonRow>
            {primary_button?.title && (
              <Link href={primary_button?.href || ''} passHref legacyBehavior>
                <Button as="a" mType="primary">
                  {primary_button?.title}
                </Button>
              </Link>
            )}

            {secondary_button?.title && (
              <Link href={secondary_button?.href || ''} passHref legacyBehavior>
                <Button as="a" mType="secondary">
                  {secondary_button?.title}
                </Button>
              </Link>
            )}
          </ButtonRow>
        </ResponsiveContainer>
      </HeroContent>
    </Hero>
  );
};

const Hero = styled.section`
  width: 100%;
  position: relative;
  min-height: 760px;
  display: flex;
  align-items: stretch;
  overflow: hidden;

  ${mediaScreen('xs', 'md')} {
    min-height: auto;
  }
`;

const VideoWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;

  .bg-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 200px 0 160px;
  display: flex;
  align-items: flex-start;

  ${mediaScreen('xs', 'md')} {
    padding: 120px 0 80px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;

  a {
    text-decoration: none;
  }

  ${mediaScreen('xs', 'md')} {
    flex-direction: column;
    gap: 16px;
    width: 100%;

    button,
    a {
      width: 100%;
      text-align: center;
    }
  }
`;
